import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiFetch } from '../fetchInterceptor';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Send, X, MessageCircle, Loader2,
  Trash2, Plus, Clock, Sparkles, Maximize2, Minimize2, ExternalLink
} from 'lucide-react';
import { cn } from '../lib/utils';
import { CBLogo } from './CBLogo';
import MarkdownView from './MarkdownView';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Floating CFO Buck Chat Widget ─── */
export default function FloatingCFOBuck() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [aiBalance, setAiBalance] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [pulse, setPulse] = useState(true);
  const [wsId, setWsId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch workspace ID on mount
  useEffect(() => {
    if (!user) return;
    const fetchWs = async () => {
      try {
        const res = await apiFetch(`${API}/api/workspaces`);
        if (res.ok) {
          const data = await res.json();
          const ws = data.workspaces?.[0] || data[0];
          if (ws) setWsId(ws.workspace_id);
        }
      } catch (e) { /* silent */ }
    };
    fetchWs();
  }, [user]);

  // Listen for external open event (from top bar button)
  useEffect(() => {
    const handler = () => { setOpen(true); setPulse(false); };
    window.addEventListener('open-cfo-chat', handler);
    return () => window.removeEventListener('open-cfo-chat', handler);
  }, []);

  // Fetch AI balance
  const fetchBalance = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiFetch(`${API}/api/dscr/ai-balance`);
      if (res.ok) setAiBalance(await res.json());
    } catch (e) { /* silent */ }
  }, [user]);

  useEffect(() => { fetchBalance(); }, [fetchBalance, open]);

  // Fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!user) return;
    try {
      const res = await apiFetch(`${API}/api/quick-chat/conversations`);
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch (e) { /* silent */ }
  }, [user]);

  useEffect(() => { if (open) fetchConversations(); }, [open, fetchConversations]);
  useEffect(() => { if (open) setPulse(false); }, [open]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (open && !showHistory) setTimeout(() => inputRef.current?.focus(), 200); }, [open, showHistory]);

  const sendMessage = async () => {
    if (!message.trim() || loading || !wsId) return;
    const userMsg = message.trim();
    setMessage('');
    setSessionError(null);
    setMessages(prev => [...prev, { role: 'user', content: userMsg, timestamp: new Date().toISOString() }]);
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/api/quick-chat/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ workspace_id: wsId, message: userMsg, conversation_id: conversationId }),
      });
      if (res.ok) {
        const data = await res.json();
        setMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: data.timestamp }]);
        if (!conversationId && data.conversation_id) setConversationId(data.conversation_id);
        fetchBalance();
      } else if (res.status === 402) {
        const err = await res.json();
        setSessionError(err.detail);
        setMessages(prev => prev.slice(0, -1));
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    }
    setLoading(false);
  };

  const loadConversation = async (convId) => {
    try {
      const res = await apiFetch(`${API}/api/quick-chat/conversations/${convId}`);
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
        setConversationId(convId);
        setShowHistory(false);
      }
    } catch (e) { /* silent */ }
  };

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await apiFetch(`${API}/api/quick-chat/conversations/${convId}`, { method: 'DELETE' });
      setConversations(prev => prev.filter(c => c.conversation_id !== convId));
      if (conversationId === convId) startNewChat();
    } catch (e) { /* silent */ }
  };

  const startNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setSessionError(null);
    setShowHistory(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  // Intercept clicks on internal links inside AI answers ("Take me there"
  // style [Open Documents](/documents)) so they navigate in-app instead of
  // opening a new tab.
  const handleMessagesClick = (e) => {
    const a = e.target.closest?.('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (href.startsWith('/')) {
      e.preventDefault();
      setOpen(false);
      navigate(href);
    }
  };

  const openFullPage = () => {
    setOpen(false);
    navigate('/chat-history');
  };

  if (!user) return null;

  // Panel sizing — when open, anchor to bottom-right; when closed render nothing.
  // Note: we used to also bail out here when `wsId` was still loading,
  // which silently broke the top-bar "CFO Buck" button if the workspaces
  // fetch hadn't returned yet (caught in Feb 2026 sign-up walk-through —
  // the user clicked the button and nothing happened). Now we always
  // mount the panel; the send-input + workspace-dependent features
  // disable themselves until wsId resolves.
  // The bottom-right floating bubble was removed because it overlapped report
  // tables. Users open CFO Buck from the top-bar "Ask CFO Buck" button which
  // dispatches the `open-cfo-chat` event.
  const panelClass = expanded
    ? "fixed bottom-20 right-2 lg:bottom-4 lg:right-4 z-50 w-[calc(100vw-1rem)] sm:w-[560px] max-h-[calc(100vh-6rem)] lg:max-h-[calc(100vh-2rem)] rounded-2xl"
    : "fixed bottom-20 right-4 lg:bottom-6 lg:right-6 z-50 w-[calc(100vw-2rem)] sm:w-[400px] max-h-[70vh] lg:max-h-[600px] rounded-2xl";

  const messagesHeight = expanded ? "min-h-[400px] max-h-[calc(100vh-12rem)]" : "min-h-[320px] max-h-[420px]";
  const historyHeight = expanded ? "min-h-[400px]" : "min-h-[320px]";

  return (
    <>
      {/* Chat Panel */}
      {open && (
        <div
          data-testid="floating-cfo-panel"
          className={cn(
            panelClass,
            "bg-card border border-border shadow-2xl",
            "flex flex-col overflow-hidden",
            "animate-in slide-in-from-bottom-5 duration-300"
          )}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-emerald-600 rounded-t-2xl flex-shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                <CBLogo size="md" />
              </div>
              <div>
                <p className="text-sm font-semibold text-white">CFO Buck</p>
                <p className="text-[10px] text-emerald-100">AI Financial Advisor</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {aiBalance && (
                <Badge variant="outline" className="text-[10px] border-white/30 text-white h-5 px-1.5" data-testid="widget-session-balance">
                  {aiBalance.ai_sessions_remaining}/{aiBalance.ai_sessions_monthly}
                </Badge>
              )}
              <button onClick={() => setExpanded(!expanded)} className="p-1.5 rounded-lg hover:bg-white/10 text-white transition-colors" title={expanded ? "Shrink" : "Expand"} data-testid="widget-expand-btn">
                {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button onClick={openFullPage} className="p-1.5 rounded-lg hover:bg-white/10 text-white transition-colors" title="Open full page" data-testid="widget-fullpage-btn">
                <ExternalLink className="w-4 h-4" />
              </button>
              <button onClick={() => setShowHistory(!showHistory)} className="p-1.5 rounded-lg hover:bg-white/10 text-white transition-colors" title="Chat History" data-testid="widget-history-btn">
                <Clock className="w-4 h-4" />
              </button>
              <button onClick={startNewChat} className="p-1.5 rounded-lg hover:bg-white/10 text-white transition-colors" title="New Chat" data-testid="widget-new-chat-btn">
                <Plus className="w-4 h-4" />
              </button>
              <button onClick={() => { setOpen(false); setExpanded(false); }} className="p-1.5 rounded-lg hover:bg-white/10 text-white transition-colors" data-testid="widget-close-btn">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Conversation History Panel */}
          {showHistory ? (
            <div className={cn("flex-1 overflow-y-auto p-3 space-y-1.5", historyHeight)} data-testid="widget-history-list">
              <div className="flex items-center justify-between px-1 mb-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Recent Conversations</p>
                <button onClick={openFullPage} className="text-[11px] text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1">
                  View All <ExternalLink className="w-3 h-3" />
                </button>
              </div>
              {conversations.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">No conversations yet</p>
              ) : (
                conversations.map(c => (
                  <div
                    key={c.conversation_id}
                    onClick={() => loadConversation(c.conversation_id)}
                    className={cn(
                      "flex items-center gap-2 p-2.5 rounded-xl cursor-pointer transition-colors group",
                      "hover:bg-muted",
                      conversationId === c.conversation_id && "bg-muted border border-border"
                    )}
                  >
                    <MessageCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{c.last_message}</p>
                    </div>
                    <button
                      onClick={(e) => deleteConversation(c.conversation_id, e)}
                      className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-all"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))
              )}
            </div>
          ) : (
            <>
              {/* Messages */}
              <div className={cn("flex-1 overflow-y-auto p-3 space-y-3", messagesHeight)} data-testid="widget-messages" onClick={handleMessagesClick}>
                {messages.length === 0 && (
                  <div className="text-center py-8">
                    <Sparkles className="w-8 h-8 mx-auto text-emerald-500/50 mb-3" />
                    <p className="text-sm font-medium text-foreground">Hi! I'm CFO Buck</p>
                    <p className="text-xs text-muted-foreground mt-1">Ask about your finances — or how to<br />do anything in CountBucks.</p>
                    <div className="flex flex-wrap justify-center gap-1.5 mt-4">
                      {["How do I upload an invoice?", "How's my cash flow?", "How do I connect my bank?", "Am I profitable?"].map(q => (
                        <button key={q} onClick={() => setMessage(q)}
                          className="text-[11px] px-2.5 py-1 rounded-full border border-border text-muted-foreground hover:text-foreground hover:border-emerald-500/30 transition-colors"
                        >{q}</button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((msg, i) => (
                  <div key={i} className={cn("flex", msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                    <div className={cn(
                      "max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed",
                      msg.role === 'user'
                        ? "bg-emerald-600 text-white rounded-br-md whitespace-pre-wrap"
                        : "bg-muted text-foreground rounded-bl-md"
                    )}>
                      {msg.role === 'user' ? msg.content : <MarkdownView>{msg.content}</MarkdownView>}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex gap-1.5">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    </div>
                  </div>
                )}
                {sessionError && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 text-xs text-amber-700 dark:text-amber-400" data-testid="widget-session-error">
                    {sessionError}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="border-t border-border p-3 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={wsId ? "Ask CFO Buck..." : "Loading your workspace…"}
                    className="flex-1 bg-muted rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-emerald-500/20 border border-border disabled:opacity-60"
                    disabled={loading || !wsId}
                    data-testid="widget-chat-input"
                  />
                  <Button onClick={sendMessage} disabled={loading || !message.trim() || !wsId} size="icon"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl h-10 w-10 flex-shrink-0" data-testid="widget-send-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
                {aiBalance && aiBalance.ai_sessions_remaining <= 5 && aiBalance.ai_sessions_remaining > 0 && (
                  <p className="text-[10px] text-amber-500 mt-1.5 px-1">
                    {aiBalance.ai_sessions_remaining} session{aiBalance.ai_sessions_remaining !== 1 ? 's' : ''} remaining this period
                  </p>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
